import {
  SelectedBundlesType,
  SelectedChecksType,
} from "./package-creation/PackageContext";

export function getBundleCheckTypesFromBundle(bundle: {
  checks?: { type: string; name?: string }[];
  check_types_with_arguments?: Record<string, unknown> | null;
}): string[] {
  const fromChecks = bundle.checks?.map((check) => check.type) || [];
  const fromArguments = Object.keys(bundle.check_types_with_arguments || {});
  return [...new Set([...fromChecks, ...fromArguments])];
}

export type BundleConflictDetail = {
  existingBundle: SelectedBundlesType;
  sharedChecks: { type: string; name: string }[];
};

function getCheckLabel(bundle: SelectedBundlesType, checkType: string): string {
  const fromChecks = bundle.checks?.find((check) => check.type === checkType);
  if (fromChecks?.name) {
    return fromChecks.name;
  }

  return checkType;
}

export function findBundleConflicts(
  incoming: SelectedBundlesType,
  existingBundles: SelectedBundlesType[]
): BundleConflictDetail[] {
  const incomingCheckTypes = new Set(getBundleCheckTypesFromBundle(incoming));

  return existingBundles.flatMap((existingBundle) => {
    const sharedTypes = getBundleCheckTypesFromBundle(existingBundle).filter(
      (checkType) => incomingCheckTypes.has(checkType)
    );

    if (sharedTypes.length === 0) {
      return [];
    }

    return [
      {
        existingBundle,
        sharedChecks: sharedTypes.map((checkType) => ({
          type: checkType,
          name: getCheckLabel(existingBundle, checkType),
        })),
      },
    ];
  });
}

export function getBundleConflictSummary(conflicts: BundleConflictDetail[]): {
  conflictingBundleTitles: string[];
  sharedCheckNames: string[];
} {
  const conflictingBundleTitles = [
    ...new Set(conflicts.map(({ existingBundle }) => existingBundle.title)),
  ];
  const sharedCheckNames = [
    ...new Set(
      conflicts.flatMap(({ sharedChecks }) =>
        sharedChecks.map((check) => check.name)
      )
    ),
  ];

  return { conflictingBundleTitles, sharedCheckNames };
}

export type CheckInBundleConflict = {
  check: { type: string; name: string };
  bundleTitles: string[];
};

function getCheckNameFromBundles(
  checkType: string,
  bundles: SelectedBundlesType[]
): string {
  for (const bundle of bundles) {
    const label = getCheckLabel(bundle, checkType);
    if (label !== checkType) {
      return label;
    }
  }

  return checkType;
}

export function findBundlesContainingCheck(
  check: { type: string; name: string },
  bundles: SelectedBundlesType[]
): CheckInBundleConflict | null {
  const matchingBundles = bundles.filter((bundle) =>
    getBundleCheckTypesFromBundle(bundle).includes(check.type)
  );

  if (matchingBundles.length === 0) {
    return null;
  }

  return {
    check: {
      type: check.type,
      name: getCheckNameFromBundles(check.type, matchingBundles) || check.name,
    },
    bundleTitles: matchingBundles.map((bundle) => bundle.title),
  };
}

export function findStandaloneChecksAbsorbedByBundle(
  incomingBundle: SelectedBundlesType,
  standaloneChecks: SelectedChecksType[]
): { type: string; name: string }[] {
  const bundleCheckTypes = new Set(
    getBundleCheckTypesFromBundle(incomingBundle)
  );

  return standaloneChecks
    .filter((check) => bundleCheckTypes.has(check.key))
    .map((check) => ({
      type: check.key,
      name: check.name,
    }));
}

export function findStandaloneChecksConflictingWithBundles(
  checks: SelectedChecksType[],
  bundles: SelectedBundlesType[]
): CheckInBundleConflict[] {
  const bundleCheckTypes = new Set(
    bundles.flatMap((bundle) => getBundleCheckTypesFromBundle(bundle))
  );

  return checks
    .filter((check) => bundleCheckTypes.has(check.key))
    .map((check) => {
      const matchingBundles = bundles.filter((bundle) =>
        getBundleCheckTypesFromBundle(bundle).includes(check.key)
      );

      return {
        check: {
          type: check.key,
          name:
            getCheckNameFromBundles(check.key, matchingBundles) || check.name,
        },
        bundleTitles: matchingBundles.map((bundle) => bundle.title),
      };
    });
}
